import { countryFlag } from './countries.js';
import { peerAcceptsChallenges } from './challenges.js';
import { formatDisplayName } from './displayName.js';
import { getPlayerProfile } from './playerProfiles.js';
import {
  isPlayerIdBoundToPeer,
  playerIdFromPeerId,
  trustedHelloNickname
} from '../security/input.js';

const HELLO_PRESENCE_TTL_MS = 2 * 60 * 60 * 1000;

function isGenericDisplayName(name) {
  return /^Anonymous peer\b/i.test(String(name || ''))
    || /^Player\b/i.test(String(name || ''));
}

function displayFromPeer(peer) {
  const playerId = peer.metadata?.playerId || playerIdFromPeerId(peer.peerId);
  const profile = playerId ? getPlayerProfile(playerId) : null;
  const nickname = peer.metadata?.nickname || profile?.nickname || '';
  const country = peer.metadata?.country || profile?.country || '';
  const enrichedPeer = {
    ...peer,
    metadata: {
      ...peer.metadata,
      playerId,
      nickname,
      country
    }
  };
  return {
    peerId: peer.peerId,
    playerId,
    name: formatDisplayName(nickname || 'Player', playerId),
    flag: countryFlag(country),
    openToChallenges: peerAcceptsChallenges(enrichedPeer),
    source: 'peer'
  };
}

export function onlinePlayersFromPeers(remotePeers = [], { selfPlayerId = '' } = {}) {
  const seen = new Set();
  const players = [];

  for (const peer of remotePeers) {
    const playerId = peer.metadata?.playerId || playerIdFromPeerId(peer.peerId);
    if (!playerId || seen.has(playerId) || playerId === selfPlayerId) {
      continue;
    }
    seen.add(playerId);
    players.push(displayFromPeer(peer));
  }

  return players;
}

export function onlinePlayersFromHellos(messages = [], { now = Date.now(), selfPlayerId = '' } = {}) {
  const latestByPlayer = new Map();

  for (const message of messages) {
    if (message?.type && message.type !== 'dechess-hello') {
      continue;
    }

    const peerId = message.senderId;
    const derivedPlayerId = playerIdFromPeerId(peerId);
    const claimedPlayerId = message.payload?.playerId;
    const playerId = claimedPlayerId && isPlayerIdBoundToPeer(claimedPlayerId, peerId)
      ? claimedPlayerId
      : derivedPlayerId;

    if (!playerId || playerId === selfPlayerId) {
      continue;
    }

    const sentAt = Date.parse(message.payload?.sentAt || '') || 0;
    const existing = latestByPlayer.get(playerId);
    if (existing && existing.sentAt >= sentAt) {
      continue;
    }

    const profile = getPlayerProfile(playerId);
    const country = message.payload?.country || profile?.country || '';

    latestByPlayer.set(playerId, {
      peerId,
      playerId,
      name: trustedHelloNickname(message),
      flag: countryFlag(country),
      openToChallenges: message.payload?.openToChallenges === undefined
        ? true
        : Boolean(message.payload.openToChallenges),
      source: 'hello',
      sentAt
    });
  }

  return [...latestByPlayer.values()].filter(
    (player) => !player.sentAt || now - player.sentAt <= HELLO_PRESENCE_TTL_MS
  );
}

function preferDisplayName(left, right) {
  if (isGenericDisplayName(left) && !isGenericDisplayName(right)) {
    return right;
  }
  if (isGenericDisplayName(right) && !isGenericDisplayName(left)) {
    return left;
  }
  return left || right;
}

function preferFlag(left, right) {
  if (left === '🏳️' && right && right !== '🏳️') {
    return right;
  }
  if (right === '🏳️' && left && left !== '🏳️') {
    return left;
  }
  return left || right;
}

function mergePlayerRecords(base, next) {
  if (!base) {
    return next;
  }
  return {
    ...base,
    ...next,
    name: preferDisplayName(base.name, next.name),
    flag: preferFlag(base.flag, next.flag),
    peerId: next.peerId || base.peerId,
    openToChallenges: base.openToChallenges !== false && next.openToChallenges !== false,
    source: base.source === 'peer' ? 'peer' : next.source
  };
}

export function mergeOnlinePlayers(remotePeers = [], helloMessages = [], options = {}) {
  const byPlayerId = new Map();

  for (const player of onlinePlayersFromPeers(remotePeers, options)) {
    byPlayerId.set(player.playerId, player);
  }
  for (const player of onlinePlayersFromHellos(helloMessages, options)) {
    byPlayerId.set(
      player.playerId,
      mergePlayerRecords(byPlayerId.get(player.playerId), player)
    );
  }

  return [...byPlayerId.values()];
}
